import { Button } from '@wedding-tech/ui/button'
import {
  ClipboardCheck,
  MapPin,
  Mic2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { domAnimation, LazyMotion, m } from 'motion/react'
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'

interface TableMapViewProps {
  tables: any[]
  highlightedTableNumber?: string | null
  isLoading?: boolean
}

const StageFlowers = () => (
  <svg
    viewBox="0 0 100 50"
    className="pointer-events-none absolute"
    style={{
      width: '75px',
      height: '38px',
    }}
  >
    {/* Dark green and sage leaves */}
    <path
      d="M10 25 C15 12, 25 12, 30 25 C25 38, 15 38, 10 25 Z"
      fill="#344C3D"
      opacity="0.8"
    />
    <path
      d="M30 35 C40 28, 45 38, 55 35 C45 42, 40 42, 30 35 Z"
      fill="#829672"
      opacity="0.95"
    />
    <path
      d="M5 30 C5 18, -5 12, -10 25 C-5 38, 5 38, 5 30 Z"
      fill="#344C3D"
      opacity="0.75"
    />
    <path
      d="M60 20 C70 15, 75 25, 85 20 C75 30, 70 30, 60 20 Z"
      fill="#829672"
      opacity="0.85"
    />

    {/* Rose 1 (center) */}
    <circle
      cx="25"
      cy="25"
      r="11"
      fill="#FDF1F2"
      stroke="#D8959B"
      strokeWidth="1"
    />
    <circle
      cx="25"
      cy="25"
      r="8"
      fill="#FFF2F4"
    />
    <path
      d="M22 22 C24 20, 26 20, 28 22"
      stroke="#D8959B"
      strokeWidth="1.3"
      fill="none"
    />
    <path
      d="M20 25 C22 28, 28 28, 30 25"
      stroke="#D8959B"
      strokeWidth="1.3"
      fill="none"
    />

    {/* Rose 2 (left) */}
    <circle
      cx="12"
      cy="28"
      r="8"
      fill="#FFF2F4"
      stroke="#D8959B"
      strokeWidth="0.8"
    />
    <circle
      cx="12"
      cy="28"
      r="5"
      fill="#FFF"
    />
    <path
      d="M10 26 C11 25, 13 25, 14 26"
      stroke="#D8959B"
      strokeWidth="1"
      fill="none"
    />

    {/* Rose 3 (right) */}
    <circle
      cx="38"
      cy="22"
      r="9"
      fill="#FDF1F2"
      stroke="#D8959B"
      strokeWidth="0.8"
    />
    <circle
      cx="38"
      cy="22"
      r="6"
      fill="#FFF2F4"
    />
    <path
      d="M36 20 C37 19, 39 19, 40 20"
      stroke="#D8959B"
      strokeWidth="1"
      fill="none"
    />
  </svg>
)

const getPointStyles = (table: any) => {
  const type = table.type || 'table'
  const shape = table.shape || 'circle'
  const customWidth =
    table.width || (type === 'stage' ? 120 : type === 'registration' ? 100 : 60)
  const customHeight =
    table.height || (type === 'stage' ? 80 : type === 'registration' ? 60 : 60)

  let borderRadius = '0px'
  if (shape === 'circle') borderRadius = '9999px'
  else if (shape === 'square' || shape === 'rectangle') borderRadius = '12px'

  switch (type) {
    case 'stage':
      return {
        className:
          'border-[#eb9fa5] bg-gradient-to-b from-[#FFF5F6] to-[#FFEBEF] text-[#D8959B] shadow-[0_4px_12px_rgba(216,149,155,0.08)] border-2',
        width: customWidth,
        height: customHeight,
        borderRadius: '16px',
        icon: <Mic2 className="size-5 text-[#D8959B]" />,
        label: 'STAGE',
      }
    case 'registration':
      return {
        className:
          'border-[#829672] bg-[#f4f3f1] text-[#344C3D] shadow-[0_4px_12px_rgba(130,150,114,0.12)] border-2',
        width: customWidth,
        height: customHeight,
        borderRadius: '18px',
        icon: <ClipboardCheck className="size-5 text-[#344C3D]" />,
        label: 'REG',
      }
    case 'other':
      return {
        className: 'border-gray-400 bg-gray-100 text-gray-600 border-2',
        width: customWidth,
        height: customHeight,
        borderRadius,
        icon: <MapPin className="size-4" />,
        label: '',
      }
    default:
      return {
        className:
          'border-gray-100 bg-white text-foreground shadow-[0_6px_20px_rgba(0,0,0,0.04)] border',
        width: customWidth,
        height: customHeight,
        borderRadius,
        icon: null,
        label: '',
      }
  }
}

const StaticTable = memo(({ table, scale, isHighlighted }: any) => {
  const pointStyles = getPointStyles(table)
  const width = table.width || pointStyles.width
  const height = table.height || pointStyles.height
  const type = table.type || 'table'
  const isTable = type === 'table'

  const capacity = table.capacity || 8

  const seats = []
  if (isTable) {
    const seatWidth = 8
    const seatHeight = 12
    const distance = width / 2 + 2
    for (let i = 0; i < capacity; i++) {
      const angle = (360 * i) / capacity
      const isEven = i % 2 === 0
      const seatBg = isEven ? 'bg-[#829672]' : 'bg-[#D8959B]'
      seats.push(
        <div
          key={i}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: `${seatWidth}px`,
            height: `${seatHeight}px`,
            marginLeft: `-${seatWidth / 2}px`,
            marginTop: `-${seatHeight / 2}px`,
            transform: `rotate(${angle}deg) translateY(-${distance}px)`,
            borderRadius: '6px 6px 2px 2px',
          }}
          className={`${seatBg} shadow-[0_1px_3px_rgba(0,0,0,0.1)]`}
        />,
      )
    }
  }

  return (
    <LazyMotion features={domAnimation}>
      <div
        style={{
          left: `${table.x}px`,
          top: `${table.y}px`,
          position: 'absolute',
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: pointStyles.borderRadius,
          zIndex: isHighlighted ? 10 : 1,
        }}
        className={`flex flex-col items-center justify-center transition-all ${
          pointStyles.className
        } ${
          isHighlighted
            ? 'ring-primary border-primary shadow-primary/30 shadow-2xl ring-3 ring-offset-2'
            : ''
        }`}
      >
        {isHighlighted && (
          <m.div
            className="bg-primary/20 absolute inset-0 -m-2 rounded-[inherit]"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}

        {/* Seats */}
        {seats}

        {/* Flower arrangements for Stage */}
        {type === 'stage' && (
          <>
            <div
              className="absolute"
              style={{ left: '-35px', top: '-15px', zIndex: 5 }}
            >
              <StageFlowers />
            </div>
            <div
              className="absolute"
              style={{
                right: '-35px',
                top: '-15px',
                zIndex: 5,
                transform: 'scaleX(-1)',
              }}
            >
              <StageFlowers />
            </div>
          </>
        )}

        {/* Stage Content */}
        {type === 'stage' && (
          <div className="flex flex-col items-center justify-center">
            {pointStyles.icon && (
              <div className="mb-0.5">{pointStyles.icon}</div>
            )}
            <span className="text-sm leading-tight font-extrabold text-[#D8959B]">
              เวที
            </span>
            <span className="mt-0.5 text-[9px] font-bold tracking-widest text-[#D8959B]/70">
              STAGE
            </span>
          </div>
        )}

        {/* Registration Content */}
        {type === 'registration' && (
          <div className="flex flex-col items-center justify-center">
            {pointStyles.icon && (
              <div className="mb-0.5">{pointStyles.icon}</div>
            )}
            <span className="text-sm leading-tight font-extrabold text-[#344C3D]">
              ลงทะเบียน
            </span>
            <span className="mt-0.5 text-[9px] font-bold tracking-widest text-[#829672]">
              REG
            </span>
          </div>
        )}

        {/* Other Content */}
        {type === 'other' && (
          <div className="flex flex-col items-center justify-center">
            {pointStyles.icon && (
              <div className="mb-1 opacity-50">{pointStyles.icon}</div>
            )}
            <span
              className="leading-none font-bold text-gray-500"
              style={{ fontSize: '14px' }}
            >
              {table.tableNumber}
            </span>
          </div>
        )}

        {/* Table Content */}
        {type === 'table' && (
          <div className="flex flex-col items-center justify-center">
            <span
              className={`leading-none font-bold text-gray-800`}
              style={{ fontSize: `16px` }}
            >
              {table.tableNumber}
            </span>
          </div>
        )}
      </div>
    </LazyMotion>
  )
})

export const TableMapView = ({
  tables,
  highlightedTableNumber,
  isLoading,
}: TableMapViewProps) => {
  const [scale, setScaleState] = useState(0.8)
  const [pan, setPanState] = useState({ x: 20, y: 20 })
  const [isPanning, setIsPanning] = useState(false)

  const scaleRef = useRef(scale)
  const panRef = useRef(pan)

  // Keep refs in sync with state
  const setScale = (s: number | ((prev: number) => number)) => {
    const nextScale = typeof s === 'function' ? s(scaleRef.current) : s
    scaleRef.current = nextScale
    setScaleState(nextScale)
  }

  const setPan = (
    p:
      | { x: number; y: number }
      | ((prev: { x: number; y: number }) => { x: number; y: number }),
  ) => {
    const nextPan = typeof p === 'function' ? p(panRef.current) : p
    panRef.current = nextPan
    setPanState(nextPan)
  }

  const viewportRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault()
      if (e.ctrlKey || e.metaKey) {
        const zoomSpeed = 0.001
        const delta = -e.deltaY * zoomSpeed
        setScale((prev) => Math.min(Math.max(0.2, prev + delta), 3))
      }
    }

    viewport.addEventListener('wheel', handleWheelNative, { passive: false })
    return () => viewport.removeEventListener('wheel', handleWheelNative)
  }, [])

  const [hasCentered, setHasCentered] = useState(false)

  // Auto-center the map on mount or when highlighted table changes
  useLayoutEffect(() => {
    if (tables.length > 0 && viewportRef.current && !hasCentered) {
      const viewport = viewportRef.current
      const vw = viewport.clientWidth
      const vh = viewport.clientHeight

      // Calculate the bounding box of all tables
      const minX = Math.min(...tables.map((t) => t.x))
      const maxX = Math.max(...tables.map((t) => t.x + (t.width || 60)))
      const minY = Math.min(...tables.map((t) => t.y))
      const maxY = Math.max(...tables.map((t) => t.y + (t.height || 60)))

      const layoutWidth = maxX - minX
      const layoutHeight = maxY - minY

      // Always fit all tables into the initial view
      const targetX = (minX + maxX) / 2
      const targetY = (minY + maxY) / 2

      // Calculate scale to fit (with padding)
      const padding = 40
      const scaleX = (vw - padding * 2) / layoutWidth
      const scaleY = (vh - padding * 2) / layoutHeight

      // Use the smaller scale to ensure everything fits, but cap it for sanity
      const finalScale = Math.min(scaleX, scaleY, 1.2)
      const safeScale = Math.max(finalScale, 0.2)

      const newX = vw / 2 - targetX * safeScale
      const newY = vh / 2 - targetY * safeScale

      setScale(safeScale)
      setPan({ x: newX, y: newY })

      // Delay setting hasCentered to true so the transition stays 'none'
      // during the initial positioning render.
      const timer = setTimeout(() => {
        setHasCentered(true)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [tables.length, hasCentered])

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.1, 2))
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.1, 0.3))
  const handleReset = () => {
    setScale(0.8)
    setHasCentered(false) // Trigger re-center
  }

  const activePointers = useRef<Map<number, { x: number; y: number }>>(
    new Map(),
  )
  const lastPinchDistance = useRef<number | null>(null)

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (activePointers.current.size === 1) {
      setIsPanning(true)
    }

    if (activePointers.current.size === 2) {
      const pointers = Array.from(activePointers.current.values())
      lastPinchDistance.current = Math.hypot(
        pointers[0].x - pointers[1].x,
        pointers[0].y - pointers[1].y,
      )
    }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const prevPos = activePointers.current.get(moveEvent.pointerId)
      if (!prevPos) return

      if (activePointers.current.size === 1) {
        // Panning
        const dx = moveEvent.clientX - prevPos.x
        const dy = moveEvent.clientY - prevPos.y
        setPan((p) => ({ x: p.x + dx, y: p.y + dy }))
      } else if (activePointers.current.size === 2) {
        // Update this pointer's position
        activePointers.current.set(moveEvent.pointerId, {
          x: moveEvent.clientX,
          y: moveEvent.clientY,
        })

        const pointers = Array.from(activePointers.current.values())

        // Calculate center of two fingers (focal point) in screen coordinates
        const focalX = (pointers[0].x + pointers[1].x) / 2
        const focalY = (pointers[0].y + pointers[1].y) / 2

        const currentDistance = Math.hypot(
          pointers[0].x - pointers[1].x,
          pointers[0].y - pointers[1].y,
        )

        if (
          lastPinchDistance.current !== null &&
          lastPinchDistance.current > 0
        ) {
          const ratio = currentDistance / lastPinchDistance.current
          const newScale = Math.min(Math.max(0.2, scaleRef.current * ratio), 3)

          if (newScale !== scaleRef.current) {
            // Adjust pan to zoom from focal point
            // newPan = focal - ((focal - oldPan) / oldScale) * newScale
            const newPanX =
              focalX -
              ((focalX - panRef.current.x) / scaleRef.current) * newScale
            const newPanY =
              focalY -
              ((focalY - panRef.current.y) / scaleRef.current) * newScale

            setPan({ x: newPanX, y: newPanY })
            setScale(newScale)
          }
        }
        lastPinchDistance.current = currentDistance
      }

      // Always update the pointer position for the next move event
      activePointers.current.set(moveEvent.pointerId, {
        x: moveEvent.clientX,
        y: moveEvent.clientY,
      })
    }

    const handlePointerUp = (upEvent: PointerEvent) => {
      activePointers.current.delete(upEvent.pointerId)
      if (activePointers.current.size < 2) {
        lastPinchDistance.current = null
      }
      if (activePointers.current.size === 0) {
        setIsPanning(false)
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
        window.removeEventListener('pointercancel', handlePointerUp)
      }
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
  }

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="border-primary size-8 animate-spin rounded-full border-3 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b bg-white/80 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold">แผนผังที่นั่ง</h2>
          {highlightedTableNumber && (
            <div className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[10px] font-bold uppercase">
              โต๊ะของคุณ: {highlightedTableNumber}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-lg bg-gray-100"
            onClick={handleZoomOut}
          >
            <ZoomOut className="size-4" />
          </Button>
          <div className="w-10 text-center text-[10px] font-bold">
            {Math.round(scale * 100)}%
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-lg bg-gray-100"
            onClick={handleZoomIn}
          >
            <ZoomIn className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 rounded-lg bg-gray-100"
            onClick={handleReset}
          >
            <RotateCcw className="size-4" />
          </Button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden bg-[#FAF8F5] select-none">
        <div
          ref={viewportRef}
          className={`h-full touch-none outline-none select-none ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
          onPointerDown={handlePointerDown}
        >
          <div
            ref={containerRef}
            className="absolute inset-0 origin-top-left"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              transition:
                isPanning || !hasCentered ? 'none' : 'transform 0.2s ease-out',
            }}
          >
            {/* Dotted Background Pattern */}
            <div
              className="pointer-events-none absolute -inset-2500"
              style={{
                backgroundImage:
                  'radial-gradient(#829672 1.3px, transparent 1px)',
                backgroundSize: '24px 24px',
                opacity: 0.12,
              }}
            />

            {tables.map((table) => (
              <StaticTable
                key={table.id}
                table={table}
                scale={scale}
                isHighlighted={table.tableNumber === highlightedTableNumber}
              />
            ))}
          </div>
        </div>

        {/* Instruction overlay */}
        <div className="pointer-events-none absolute right-4 bottom-4 left-4 flex justify-center">
          <div className="rounded-full bg-black/50 px-4 py-2 text-[10px] font-bold text-white backdrop-blur-md">
            ใช้ 2 นิ้วเพื่อย่อ/ขยาย หรือลากเพื่อเลื่อนดูแผนที่
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 overflow-x-auto border-t bg-white p-4 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <div className="mr-1 flex items-center gap-0.5">
            <div className="size-1 rounded-full bg-[#829672]" />
            <div className="size-3.5 rounded-full border border-gray-300 bg-white" />
            <div className="size-1 rounded-full bg-[#D8959B]" />
          </div>
          <span className="text-[10px] font-bold text-gray-500 uppercase">
            โต๊ะอาหาร
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="size-3.5 rounded-md border-2 border-[#eb9fa5] bg-gradient-to-b from-[#FFF5F6] to-[#FFEBEF]" />
          <span className="text-[10px] font-bold text-gray-500 uppercase">
            เวที
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="size-3.5 rounded-md border-2 border-[#829672] bg-[#f4f3f1]" />
          <span className="text-[10px] font-bold text-gray-500 uppercase">
            ลงทะเบียน
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="ring-primary bg-primary/20 size-2.5 animate-pulse rounded-full ring-2" />
          <span className="text-primary text-[10px] font-bold uppercase">
            ที่นั่งของคุณ
          </span>
        </div>
      </div>
    </div>
  )
}
